// App entry: tap to arm, then watch devicemotion for freefall.
//
// The tap serves two purposes: iOS requires motion-sensor permission
// to be requested from a user gesture, and autoplay policy requires a
// gesture before audio can play unattended.

import { SCREAM_HOLD_MS } from './constants.js'
import { recordSample } from './motion.js'
import { renderMode } from './pages.js'
import { playScream, unlockAudio } from './scream.js'

// While set, the freefall screen is being held on-screen.
let holdTimer: number | undefined

/*
 * React to one devicemotion sample; scream and hold the freefall
 * screen when the detection window fills.
 */
const onMotion = (event: DeviceMotionEvent) => {
  if (!recordSample(event)) {
    if (holdTimer === undefined) {
      renderMode('normal')
    }
    return
  }

  playScream()
  renderMode('freefall')

  clearTimeout(holdTimer)
  holdTimer = window.setTimeout(() => {
    holdTimer = undefined
    renderMode('normal')
  }, SCREAM_HOLD_MS)
}

/*
 * Ask iOS 13+ for motion access; other platforms need no permission.
 */
const requestMotionPermission = async (): Promise<boolean> => {
  const motionEvent = DeviceMotionEvent as unknown as {
    requestPermission?: () => Promise<string>
  }

  if (typeof motionEvent.requestPermission !== 'function') {
    return true
  }

  try {
    const response = await motionEvent.requestPermission()
    return response === 'granted'
  } catch {
    return false
  }
}

/*
 * First tap: unlock audio, request motion access, start listening.
 */
const onArm = async () => {
  await unlockAudio()

  const granted = await requestMotionPermission()
  if (!granted) {
    renderMode('denied')
    return
  }

  window.addEventListener('devicemotion', onMotion)
  renderMode('normal')
}

const registerServiceWorker = () => {
  if ('serviceWorker' in navigator && location.hostname !== 'localhost') {
    navigator.serviceWorker.register('/sw.js').catch(err => {
      console.error(`failed to register service-worker: ${err.message}`)
    })
  }
}

const main = () => {
  registerServiceWorker()

  if (!('DeviceMotionEvent' in window)) {
    renderMode('unsupported')
    return
  }

  renderMode('waiting')
  document.body.addEventListener('click', onArm, { once: true })
}

main()
